import React, { useState, useEffect, useCallback } from 'react'
import { priceFormat } from '../utils/helper'
import { logCard } from '../utils/cardPaymentLog'

const TeyaRefundModal = ({ show, amount, gatewayPaymentId, onSuccess, onCancel }) => {
    const [status, setStatus] = useState('INIT') // INIT, PROCESSING, SUCCESS, FAILED
    const [errorMessage, setErrorMessage] = useState('')

    const initiateRefund = useCallback(async () => {
        setStatus('PROCESSING')
        setErrorMessage('')

        logCard('REFUND_REQ', `Pokretanje refunda iznos=${amount} gpid=${gatewayPaymentId}`, {
            amount,
            gateway_payment_id: gatewayPaymentId,
        })

        try {
            const { data } = await window.axios.post('/card-processor/refund', {
                amount: amount,
                gateway_payment_id: gatewayPaymentId,
            })

            logCard('REFUND_RESP', `success=${data?.success}`, {
                success: data?.success,
                error: data?.error,
                refund_id: data?.refund_id,
            })

            if (!data.success) {
                setStatus('FAILED')
                setErrorMessage(data.error || 'Greška pri povratu')
                return
            }

            setStatus('SUCCESS')
            // Ne pozivaj onSuccess odmah - neka korisnik vidi poruku
            // onSuccess će se pozvati iz "Zatvori" gumba
        } catch (err) {
            const msg = err.response?.data?.error || err.message || 'Greška pri komunikaciji s Teya API-jem'
            logCard('REFUND_ERR', msg, {
                http_status: err?.response?.status,
                response_data: err?.response?.data,
                gateway_payment_id: gatewayPaymentId,
                amount,
            })
            setStatus('FAILED')
            setErrorMessage(msg)
        }
    }, [amount, gatewayPaymentId, onSuccess])

    useEffect(() => {
        if (show && amount > 0 && gatewayPaymentId) {
            initiateRefund()
        }
    }, [show]) // eslint-disable-line react-hooks/exhaustive-deps

    const handleRetry = () => {
        initiateRefund()
    }

    const handleClose = () => {
        if (onCancel) onCancel()
    }

    if (!show) return null

    return (
        <div className="fixed w-full h-screen left-0 top-0 z-50 flex flex-wrap justify-center content-center p-24">
            <div className="fixed w-full h-screen left-0 top-0 z-0 bg-black bg-opacity-60"></div>
            <div className="w-96 rounded-lg bg-white shadow-xl overflow-hidden z-10">
                <div className="p-6 text-center">
                    <div className="mb-4">
                        <h3 className="text-lg font-semibold text-red-700">Povrat na karticu</h3>
                        <p className="text-2xl font-bold text-gray-900 mt-1">{priceFormat(amount)}</p>
                    </div>

                    <div className="my-6">
                        {(status === 'INIT' || status === 'PROCESSING') && (
                            <>
                                <div className="flex justify-center mb-4">
                                    <div className="w-12 h-12 border-4 border-gray-200 border-t-red-500 rounded-full animate-spin"></div>
                                </div>
                                <p className="text-gray-600 font-medium">Obrađujem povrat...</p>
                            </>
                        )}

                        {status === 'SUCCESS' && (
                            <>
                                <div className="flex justify-center mb-4">
                                    <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                                        <svg className="w-8 h-8 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                                        </svg>
                                    </div>
                                </div>
                                <p className="text-green-600 font-semibold">Povrat uspješan!</p>
                                <p className="text-sm text-gray-400 mt-1">Novac će biti vraćen na karticu kupca.</p>
                            </>
                        )}

                        {status === 'FAILED' && (
                            <>
                                <div className="flex justify-center mb-4">
                                    <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                                        <svg className="w-8 h-8 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        </svg>
                                    </div>
                                </div>
                                <p className="text-red-600 font-semibold">Povrat nije uspio</p>
                                {errorMessage && (
                                    <p className="text-sm text-red-400 mt-1">{errorMessage}</p>
                                )}
                            </>
                        )}
                    </div>

                    <div className="flex gap-2 mt-4">
                        {status === 'FAILED' && (
                            <>
                                <button
                                    onClick={handleRetry}
                                    className="flex-1 py-2 px-4 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition-colors"
                                >
                                    Pokušaj ponovo
                                </button>
                                <button
                                    onClick={handleClose}
                                    className="flex-1 py-2 px-4 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg font-medium transition-colors"
                                >
                                    Odustani
                                </button>
                            </>
                        )}

                        {status === 'SUCCESS' && (
                            <button
                                onClick={() => { if (onSuccess) onSuccess(); }}
                                className="flex-1 py-2 px-4 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition-colors"
                            >
                                U redu
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    )
}

export default TeyaRefundModal
